import type { ApiClient } from '../client.js';
import { ENTITY_DIRS, type ConfigDiff, type DatabaseConfig, type DatabaseMeta, type EntityKind } from './types.js';

export interface DatabaseRecord {
  id: string;
  environment?: string;
  integrations?: string;
  origin_database_id?: string | null;
  org_id?: string;
  name?: string;
  description?: string;
}

/** Map a server `DatabaseRecord` to the on-disk `DatabaseMeta`, falling back to `fallbackId` for the id. */
export function metaFromRecord(record: DatabaseRecord, fallbackId: string): DatabaseMeta {
  return {
    database_id: record.id ?? fallbackId,
    origin_database_id: record.origin_database_id ?? undefined,
    name: record.name,
    description: record.description,
    environment: record.environment,
    integrations: record.integrations,
  };
}

export interface ApplyResult {
  diff: ConfigDiff;
  new_trigger_secrets: Record<string, string>;
  new_inbound_webhook_secrets: Record<string, string>;
}

export function getDatabaseRecord(client: ApiClient, id: string): Promise<DatabaseRecord> {
  return client.request<DatabaseRecord>('GET', `/v1/databases/${id}`);
}

export function getConfig(client: ApiClient, id: string): Promise<DatabaseConfig> {
  return client.request<DatabaseConfig>('GET', `/v1/${id}/config`);
}

export function diffConfig(client: ApiClient, id: string, local: DatabaseConfig): Promise<ConfigDiff> {
  return client.request<ConfigDiff>('POST', `/v1/${id}/config/diff`, local);
}

export function applyConfig(client: ApiClient, id: string, local: DatabaseConfig): Promise<ApplyResult> {
  return client.request<ApplyResult>('PUT', `/v1/${id}/config`, local);
}

export function createPreview(client: ApiClient, originId: string, name: string, description?: string): Promise<DatabaseRecord> {
  return client.request<DatabaseRecord>('POST', `/v1/${originId}/preview`, {
    name,
    ...(description ? { description } : {}),
  });
}

/** Delete one config entity via its per-entity route (used by `push --prune`). */
export function deleteEntity(client: ApiClient, databaseId: string, kind: EntityKind, id: string): Promise<unknown> {
  return client.request('DELETE', `/v1/${databaseId}/${ENTITY_DIRS[kind]}/${id}`);
}
